"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { adaptMenuData, MenuNode } from "../header/v2/menuAdapter";
import { fetchFooterMenu } from "@/api-service/footer";
import { fetchAllPages } from "@/api-service/pages";
import { useAuth } from "@/packages/context/AuthContext";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import { fetchGeneralSettings } from "@/api-service/general";
import type { GeneralSettingsResponse } from "@/packages/types/general";
import type { PagesListApiResponse } from "@/packages/types/page";
import LABELS from "@/components/global-constant/src/general";
// import { Swiper, SwiperSlide } from "swiper/react";
// import { Autoplay } from "swiper/modules";
import "swiper/css";

// Fallback hierarchical categories matching DistaCart layout structure
const fallbackCategories: MenuNode[] = [
  {
    id: "wellness",
    label: "Top Wellness Products",
    href: "/collections/wellness",
    type: 2,
    children: [
      {
        id: "capsules",
        label: "Herbal Capsules",
        href: "/collections/capsules",
        type: 2,
        children: [
          { id: "neem", label: "Neem", href: "/collections/neem", type: 2 },
          { id: "turmeric", label: "Turmeric", href: "/collections/turmeric", type: 2 },
          { id: "giloy", label: "Giloy", href: "/collections/giloy", type: 2 }
        ]
      },
      {
        id: "tablets",
        label: "Ayurvedic Tablets",
        href: "/collections/tablets",
        type: 2,
        children: [
          { id: "liv52", label: "Liv 52", href: "/collections/liv-5-2", type: 2 },
          { id: "mukta", label: "Mukta Vati", href: "/collections/mukta-vati", type: 2 }
        ]
      }
    ]
  },
  {
    id: "beauty",
    label: "Top Indian Beauty Products",
    href: "/collections/beauty",
    type: 2,
    children: [
      {
        id: "hair",
        label: "Hair Serums",
        href: "/collections/hair-serums",
        type: 2,
        children: [
          { id: "wishcare", label: "Wishcare", href: "/collections/wishcare", type: 2 },
          { id: "growth", label: "Hair Growth", href: "/collections/hair-growth", type: 2 }
        ]
      },
      {
        id: "skincare",
        label: "Skin Lightening",
        href: "/collections/skincare",
        type: 2,
        children: [
          { id: "vaseline", label: "Vaseline", href: "/collections/vaseline", type: 2 },
          { id: "bodywise", label: "Bodywise", href: "/collections/bodywise", type: 2 }
        ]
      }
    ]
  },
  {
    id: "food",
    label: "Top Indian Food Products",
    href: "/collections/food",
    type: 2,
    children: [
      {
        id: "sweets",
        label: "Assorted Sweets",
        href: "/collections/sweets",
        type: 2,
        children: [
          { id: "pulla-reddy", label: "Pulla Reddy", href: "/collections/pulla-reddy", type: 2 },
          { id: "ariselu", label: "Vellanki Foods", href: "/collections/vellanki-foods", type: 2 }
        ]
      },
      {
        id: "staples",
        label: "Ghee & Powders",
        href: "/collections/ghee",
        type: 2,
        children: [
          { id: "cow-ghee", label: "A2 Cow Ghee", href: "/collections/cow-ghee", type: 2 },
          { id: "ensure", label: "Ensure Vanilla", href: "/collections/ensure", type: 2 }
        ]
      }
    ]
  },
  {
    id: "baby",
    label: "Top Baby & Kids Products",
    href: "/collections/baby",
    type: 2,
    children: [
      {
        id: "milk",
        label: "Infant Formulas",
        href: "/collections/baby-formulas",
        type: 2,
        children: [
          { id: "nan-pro", label: "Nan Pro 3", href: "/collections/nan-pro", type: 2 },
          { id: "similac", label: "Similac Soy", href: "/collections/similac", type: 2 }
        ]
      },
      {
        id: "cereals",
        label: "Kids Nutrition",
        href: "/collections/kids-nutrition",
        type: 2,
        children: [
          { id: "ceregrow", label: "Ceregrow", href: "/collections/ceregrow", type: 2 },
          { id: "pediasure", label: "Pediasure", href: "/collections/pediasure", type: 2 }
        ]
      }
    ]
  }
];

// Interactive Serving States & Districts Data
const citiesData: Record<string, string> = {
  "Tamil Nadu": "Ariyalur, Chengalpattu, Chennai, Coimbatore, Cuddalore, Dharmapuri, Dindigul, Erode, Kallakurichi, Kanchipuram, Kanyakumari, Karur, Krishnagiri, Madurai, Nagapattinam, Namakkal, Nilgiris, Perambalur, Pudukkottai, Ramanathapuram, Ranipet, Salem, Sivaganga, Tenkasi, Thanjavur, Theni, Thoothukudi (Tuticorin), Tiruchirappalli, Tirunelveli, Tirupathur, Tiruppur, Tiruvallur, Tiruvannamalai, Tiruvarur, Vellore, Viluppuram, Virudhunagar",
  "Andaman & Nicobar": "Nicobar, North and Middle Andaman, South Andaman",
  "Andhra Pradesh": "Anantapur, Chittoor, East Godavari, Guntur, Krishna, Kurnool, Prakasam, Srikakulam, Sri Potti Sriramulu Nellore, Visakhapatnam, Vizianagaram, West Godavari, YSR Kadapa",
  "Arunachal Pradesh": "Anjaw, Changlang, Dibang Valley, East Kameng, East Siang, Kamle, Kra Daadi, Kurung Kumey, Lepa Rada, Lohit, Longding, Lower Dibang Valley, Lower Siang, Lower Subansiri, Namsai, Pakke Kessang, Papum Pare, Shi Yomi, Siang, Tawang, Tirap, Upper Siang, Upper Subansiri, West Kameng, West Siang",
  "Assam": "Baksa, Barpeta, Biswanath, Bongaigaon, Cachar, Charaideo, Chirang, Darrang, Dhemaji, Dhubri, Dibrugarh, Dima Hasao (North Cachar Hills), Goalpara, Golaghat, Hailakandi, Hojai, Jorhat, Kamrup, Kamrup Metropolitan, Karbi Anglong, Karimganj, Kokrajhar, Lakhimpur, Majuli, Morigaon, Nagaon, Nalbari, Sivasagar, Sonitpur, South Salamara-Mankachar, Tinsukia, Udalguri, West Karbi Anglong",
  "Bihar": "Araria, Arwal, Aurangabad, Banka, Begusarai, Bhagalpur, Bhojpur, Buxar, Darbhanga, East Champaran (Motihari), Gaya, Gopalganj, Jamui, Jehanabad, Kaimur (Bhabua), Katihar, Khagaria, Kishanganj, Lakhisarai, Madhepura, Madhubani, Munger (Monghyr), Muzaffarpur, Nalanda, Nawada, Patna, Purnia (Purnea), Rohtas, Saharsa, Samastipur, Saran, Sheikhpura, Sheohar, Sitamarhi, Siwan, Supaul, Vaishali, West Champaran",
  "Chandigarh": "Chandigarh",
  "Chhattisgarh": "Balod, Baloda Bazar, Balrampur, Bastar, Bemetara, Bijapur, Bilaspur, Dantewada (South Bastar), Dhamtari, Durg, Gariyaband, Janjgir-Champa, Jashpur, Kabirdham (Kawardha), Kanker (North Bastar), Kondagaon, Korba, Korea (Koriya), Mahasamund, Mungeli, Narayanpur, Raigarh, Raipur, Rajnandgaon, Sukma, Surajpur, Surguja",
  "Dadra & Nagar Haveli": "Dadra & Nagar Haveli",
  "Daman & Diu": "Daman, Diu",
  "Delhi (NCT)": "Central Delhi, East Delhi, New Delhi, North Delhi, North East Delhi, North West Delhi, Shahdara, South Delhi, South East Delhi, South West Delhi, West Delhi",
  "Goa": "North Goa, South Goa",
  "Gujarat": "Ahmedabad, Amreli, Anand, Aravalli, Banaskantha (Palanpur), Bharuch, Bhavnagar, Botad, Chhota Udepur, Dahod, Dangs (Ahwa), Devbhoomi Dwarka, Gandhinagar, Gir Somnath, Jamnagar, Junagadh, Kachchh, Kheda (Nadiad), Mahisagar, Mehsana, Morbi, Narmada (Rajpipla), Navsari, Panchmahal (Godhra), Patan, Porbandar, Rajkot, Sabarkantha (Himmatnagar), Surat, Surendranagar, Tapi (Vyara), Vadodara, Valsad",
  "Haryana": "Ambala, Bhiwani, Charkhi Dadri, Faridabad, Fatehabad, Gurugram (Gurgaon), Hisar, Jhajjar, Jind, Kaithal, Karnal, Kurukshetra, Mahendragarh, Nuh, Palwal, Panchkula, Panipat, Rewari, Rohtak, Sirsa, Sonipat, Yamunanagar",
  "Himachal Pradesh": "Bilaspur, Chamba, Hamirpur, Kangra, Kinnaur, Kullu, Lahaul & Spiti, Mandi, Shimla, Sirmaur (Sirmour), Solan, Una",
  "Jammu & Kashmir": "Anantnag, Bandipore, Baramulla, Budgam, Doda, Ganderbal, Jammu, Kathua, Kishtwar, Kulgam, Kupwara, Poonch, Pulwama, Rajouri, Ramban, Reasi, Samba, Shopian, Srinagar, Udhampur",
  "Jharkhand": "Bokaro, Chatra, Deoghar, Dhanbad, Dumka, East Singhbhum, Garhwa, Giridih, Godda, Gumla, Hazaribag, Jamtara, Khunti, Koderma, Latehar, Lohardaga, Pakur, Palamu, Ramgarh, Ranchi, Sahibganj, Seraikela-Kharsawan, Simdega, West Singhbhum",
  "Karnataka": "Bagalkot, Ballari (Bellary), Belagavi (Belgaum), Bengaluru (Bangalore) Rural, Bengaluru (Bangalore) Urban, Bidar, Chamarajanagar, Chikballapur, Chikkamagaluru (Chikmagalur), Chitradurga, Dakshina Kannada, Davangere, Dharwad, Gadag, Hassan, Haveri, Kalaburagi (Gulbarga), Kodagu, Kolar, Koppal, Mandya, Mysuru (Mysore), Raichur, Ramanagara, Shivamogga (Shimoga), Tumakuru (Tumkur), Udupi, Uttara Kannada (Karwar), Vijayapura (Bijapur), Yadgir",
  "Kerala": "Alappuzha, Ernakulam, Idukki, Kannur, Kasaragod, Kollam, Kottayam, Kozhikode, Malappuram, Palakkad, Pathanamthitta, Thiruvananthapuram, Thrissur, Wayanad",
  "Ladakh": "Kargil, Leh",
  "Lakshadweep": "Lakshadweep",
  "Madhya Pradesh": "Agar Malwa, Alirajpur, Anuppur, Ashoknagar, Balaghat, Barwani, Betul, Bhind, Bhopal, Burhanpur, Chhatarpur, Chhindwara, Damoh, Datia, Dewas, Dhar, Dindori, Guna, Gwalior, Harda, Hoshangabad, Indore, Jabalpur, Jhabua, Katni, Khandwa, Khargone, Mandla, Mandsaur, Morena, Narsinghpur, Neemuch, Panna, Raisen, Rajgarh, Ratlam, Rewa, Sagar, Satna, Sehore, Seoni, Shahdol, Shajapur, Sheopur, Shivpuri, Sidhi, Singrauli, Tikamgarh, Ujjain, Umaria, Vidisha",
  "Maharashtra": "Ahmednagar, Akola, Amravati, Aurangabad, Beed, Bhandara, Buldhana, Chandrapur, Dhule, Gadchiroli, Gondia, Hingoli, Jalgaon, Jalna, Kolhapur, Latur, Mumbai City, Mumbai Suburban, Nagpur, Nanded, Nandurbar, Nashik, Osmanabad, Palghar, Parbhani, Pune, Raigad, Ratnagiri, Sangli, Satara, Sindhudurg, Solapur, Thane, Wardha, Washim, Yavatmal",
  "Manipur": "Bishnupur, Chandel, Churachandpur, Imphal East, Imphal West, Jiribam, Kakching, Kamjong, Kangpokpi, Noney, Pherzawl, Senapati, Tamenglong, Tengnoupal, Thoubal, Ukhrul",
  "Meghalaya": "East Garo Hills, East Jaintia Hills, East Khasi Hills, North Garo Hills, Ri Bhoi, South Garo Hills, South West Garo Hills, South West Khasi Hills, West Garo Hills, West Jaintia Hills, West Khasi Hills",
  "Mizoram": "Aizawl, Champhai, Kolasib, Lawngtlai, Lunglei, Mamit, Saiha, Serchhip",
  "Nagaland": "Dimapur, Kiphire, Kohima, Longleng, Mokokchung, Mon, Peren, Phek, Tuensang, Wokha, Zunheboto",
  "Odisha": "Angul, Balangir, Balasore, Bargarh, Bhadrak, Boudh, Cuttack, Deogarh, Dhenkanal, Gajapati, Ganjam, Jagatsinghapur, Jajpur, Jharsuguda, Kalahandi, Kandhamal, Kendrapara, Kendujhar (Keonjhar), Khordha, Koraput, Malkangiri, Mayurbhanj, Nabarangpur, Nayagarh, Nuapada, Puri, Rayagada, Sambalpur, Sonepur, Sundargarh",
  "Puducherry": "Karaikal, Mahe, Pondicherry, Yanam",
  "Punjab": "Amritsar, Barnala, Bathinda, Faridkot, Fatehgarh Sahib, Fazilka, Ferozepur, Gurdaspur, Hoshiarpur, Jalandhar, Kapurthala, Ludhiana, Mansa, Moga, Muktsar, Nawanshahr (Shahid Bhagat Singh Nagar), Pathankot, Patiala, Rupnagar, Sahibzada Ajit Singh Nagar (Mohali), Sangrur, Tarn Taran",
  "Rajasthan": "Ajmer, Alwar, Banswara, Baran, Barmer, Bharatpur, Bhilwara, Bikaner, Bundi, Chittorgarh, Churu, Dausa, Dholpur, Dungarpur, Hanumangarh, Jaipur, Jaisalmer, Jalore, Jhalawar, Jhunjhunu, Jodhpur, Karauli, Kota, Nagaur, Pali, Pratapgarh, Rajsamand, Sawai Madhopur, Sikar, Sirohi, Sri Ganganagar, Tonk, Udaipur",
  "Sikkim": "East Sikkim, North Sikkim, South Sikkim, West Sikkim",
  "Telangana": "Adilabad, Bhadradri Kothagudem, Hyderabad, Jagtial, Jangaon, Jayashankar Bhoopalpally, Jogulamba Gadwal, Kamareddy, Karimnagar, Khammam, Komaram Bheem Asifabad, Mahabubabad, Mahabubnagar, Mancherial, Medak, Medchal, Nagarkurnool, Nalgonda, Nirmal, Nizamabad, Peddapalli, Rajanna Sircilla, Rangareddy, Sangareddy, Siddipet, Suryapet, Vikarabad, Wanaparthy, Warangal (Rural), Warangal (Urban), Yadadri Bhuvanagiri",
  "Tripura": "Dhalai, Gomati, Khowai, North Tripura, Sepahijala, South Tripura, Unakoti, West Tripura",
  "Uttarakhand": "Almora, Bageshwar, Chamoli, Champawat, Dehradun, Haridwar, Nainital, Pauri Garhwal, Pithoragarh, Rudraprayag, Tehri Garhwal, Udham Singh Nagar, Uttarkashi",
  "Uttar Pradesh": "Agra, Aligarh, Allahabad, Ambedkar Nagar, Amethi (Chatrapati Sahuji Mahraj Nagar), Amroha (J.P. Nagar), Auraiya, Azamgarh, Baghpat, Bahraich, Ballia, Balrampur, Banda, Barabanki, Bareilly, Basti, Bhadohi, Bijnor, Budaun, Bulandshahr, Chandauli, Chitrakoot, Deoria, Etah, Etawah, Faizabad, Farrukhabad, Fatehpur, Firozabad, Gautam Buddha Nagar, Ghaziabad, Ghazipur, Gonda, Gorakhpur, Hamirpur, Hapur (Panchsheel Nagar), Hardoi, Hathras, Jalaun, Jaunpur, Jhansi, Kannauj, Kanpur Dehat, Kanpur Nagar, Kanshiram Nagar (Kasganj), Kaushambi, Kushinagar (Padrauna), Lakhimpur - Kheri, Lalitpur, Lucknow, Maharajganj, Mahoba, Mainpuri, Mathura, Mau, Meerut, Mirzapur, Moradabad, Muzaffarnagar, Pilibhit, Pratapgarh, RaeBareli, Rampur, Saharanpur, Sambhal (Bhim Nagar), Sant Kabir Nagar, Shahjahanpur, Shamali (Prabuddh Nagar), Shravasti, Siddharth Nagar, Sitapur, Sonbhadra, Sultanpur, Unnao, Varanasi",
  "West Bengal": "Alipurduar, Bankura, Birbhum, Cooch Behar, Dakshin Dinajpur (South Dinajpur), Darjeeling, Hooghly, Howrah, Jalpaiguri, Jhargram, Kalimpong, Kolkata, Malda, Murshidabad, Nadia, North 24 Parganas, Paschim Medinipur (West Medinipur), Paschim (West) Burdwan (Bardhaman), Purba Burdwan (Bardhaman), Purba Medinipur (East Medinipur), Purulia, South 24 Parganas, Uttar Dinajpur (North Dinajpur)"
};

const decodeHtml = (html: string) => {
  if (typeof window === "undefined") return html;
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value.replace(/\u00A0/g, " ").replace(/&nbsp;/g, " ");
};

export default function FooterV2() {
  const { isLoggedIn } = useAuth();
  const { fastPush } = useFastRouter();
  const [categories, setCategories] = useState<MenuNode[]>([]);
  const [activeCityTab, setActiveCityTab] = useState<string>("Tamil Nadu");
  const [settings, setSettings] = useState<GeneralSettingsResponse["data"] | null>(null);
  const [pages, setPages] = useState<PagesListApiResponse["data"]>([]);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      try {
        const res = await fetchFooterMenu();
        if (isMounted && res?.data) {
          const { nodes } = adaptMenuData(res.data);
          setCategories(nodes?.length > 0 ? nodes : fallbackCategories);
        } else {
          setCategories(fallbackCategories);
        }
      } catch (err) {
        console.error("Failed to load footer categories", err);
        if (isMounted) setCategories(fallbackCategories);
      }
    };

    const loadSettings = async () => {
      try {
        const res = await fetchGeneralSettings();
        if (isMounted && res?.data) {
          setSettings(res.data);
        }
      } catch (err) {
        console.error("Failed to load footer settings", err);
      }
    };

    const loadPages = async () => {
      try {
        const data = await fetchAllPages();
        if (isMounted) {
          setPages(data);
        }
      } catch (err) {
        console.error("Failed to load footer pages", err);
      }
    };

    loadCategories();
    loadSettings();
    loadPages();

    return () => {
      isMounted = false;
    };
  }, []);

  // const renderStars = (rating: number) => {
  //   const fullStars = Math.floor(rating);
  //   const hasHalf = rating % 1 !== 0;
  //   return (
  //     <div className="flex items-center gap-0.5 text-amber-400">
  //       {[...Array(5)].map((_, i) => {
  //         if (i < fullStars) {
  //           return (
  //             <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
  //               <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  //             </svg>
  //           );
  //         } else if (i === fullStars && hasHalf) {
  //           return (
  //             <svg key={i} className="w-4 h-4 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
  //               <defs>
  //                 <linearGradient id={`half-star-${rating}`}>
  //                   <stop offset="50%" stopColor="currentColor" />
  //                   <stop offset="50%" stopColor="#E2E8F0" />
  //                 </linearGradient>
  //               </defs>
  //               <path fill={`url(#half-star-${rating})`} d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  //             </svg>
  //           );
  //         } else {
  //           return (
  //             <svg key={i} className="w-4 h-4 text-slate-200 fill-current" viewBox="0 0 20 20">
  //               <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  //             </svg>
  //           );
  //         }
  //       })}
  //     </div>
  //   );
  // };

  const currentCategories = categories.length > 0 ? categories : fallbackCategories;

  return (
    <footer className="w-full bg-[#f8fafc] border-t border-slate-200 text-slate-700 mt-4 pt-10 pb-0 font-sans" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 space-y-10">


        <hr className="border-slate-200" />

        {/* Section 2: Core Info Columns (About, Categories, Policies, Account, Cities) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-8 items-start">
          
          {/* Col 1: About Us */}
          <div className="hidden md:block space-y-4 col-span-1 lg:col-span-2">
            <h4 className="font-semibold text-slate-950 text-[15px] uppercase tracking-wider">
              {LABELS.FOOTER.ABOUT_TITLE}
            </h4>
            <div className="space-y-2 leading-relaxed text-sm text-slate-500 text-justify">
              <div className="relative group">
                <div className="max-h-[160px] overflow-hidden relative transition-all duration-300">
                  {settings?.general_settings?.about_us ? (
                    <div 
                      className="text-xs leading-relaxed break-words whitespace-normal"
                      style={{ wordBreak: "break-word", overflowWrap: "break-word", whiteSpace: "normal" }}
                      dangerouslySetInnerHTML={{ __html: decodeHtml(settings.general_settings.about_us) }}
                    />
                  ) : (
                    <p className="text-xs">
                      {LABELS.BRAND.ABOUT_FALLBACK}
                    </p>
                  )}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#f8fafc] to-transparent" />
                </div>
                <button
                  type="button"
                  onClick={() => setIsAboutModalOpen(true)}
                  className="mt-1 text-[11px] font-bold text-[var(--brand-accent)] hover:underline inline-flex items-center gap-1 cursor-pointer transition-colors"
                >
                  Read More...
                </button>
              </div>

              <p className="text-[10px] font-semibold text-slate-400">
                {LABELS.BRAND.NAME} is a product of {LABELS.BRAND.PRODUCT_OF}.
              </p>
            </div>
          </div>

          {/* Col 2: Categories */}
          <div className="hidden md:block space-y-4 col-span-1">
            <h4 className="font-semibold text-slate-950 text-[15px] uppercase tracking-wider">
              {LABELS.FOOTER.CATEGORIES_TITLE}
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-500">
              {currentCategories.map((cat) => (
                <li key={cat.id}>
                  <Link href={cat.href} className="hover:text-[var(--brand-accent)] transition-colors text-xs">
                    {cat.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Policies & Terms */}
          <div className="space-y-4 col-span-1">
            <h4 className="font-semibold text-slate-950 text-[15px] uppercase tracking-wider">
              {LABELS.FOOTER.POLICIES_TITLE}
            </h4>
            <ul className="grid grid-cols-3 md:grid-cols-1 gap-x-3 gap-y-2.5 text-sm text-slate-500">
              {pages.map((page) => (
                <li key={page.id}>
                  <Link
                    href={`/pages/${page.slug}`}
                    className="hover:text-[var(--brand-accent)] transition-colors text-xs"
                  >
                    {page.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Account */}
          <div className="hidden md:block space-y-4 col-span-1">
            <h4 className="font-semibold text-slate-950 text-[15px] uppercase tracking-wider">
              {LABELS.FOOTER.ACCOUNT_TITLE}
            </h4>
            <ul className="space-y-2.5 text-sm text-slate-500">
              {isLoggedIn ? (
                <>
                  <li><Link href="/user-profile" className="hover:text-[var(--brand-accent)] transition-colors text-xs">Personal Info</Link></li>
                  <li><Link href="/user-profile?tab=orders" className="hover:text-[var(--brand-accent)] transition-colors text-xs">Orders</Link></li>
                </>
              ) : (
                <>
                  <li>
                    <span
                      onClick={() => fastPush("/sign-in")}
                      className="hover:text-[var(--brand-accent)] transition-colors text-xs cursor-pointer"
                    >
                      Login
                    </span>
                  </li>
                  <li>
                    <span
                      onClick={() => fastPush("/sign-up")}
                      className="hover:text-[var(--brand-accent)] transition-colors text-xs cursor-pointer"
                    >
                      Register
                    </span>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Col 5: Major cities serving */}
          <div className="hidden md:block space-y-4 col-span-1 lg:col-span-2">
            <h4 className="font-semibold text-slate-950 text-[15px] uppercase tracking-wider">
              {LABELS.FOOTER.SERVING_LOCATIONS_TITLE}
            </h4>
            <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)] flex flex-col h-[240px]">
              <style dangerouslySetInnerHTML={{ __html: `
                .custom-thin-scrollbar::-webkit-scrollbar {
                  width: 4px;
                  height: 4px;
                }
                .custom-thin-scrollbar::-webkit-scrollbar-track {
                  background: #F8FAFC;
                  border-radius: 2px;
                }
                .custom-thin-scrollbar::-webkit-scrollbar-thumb {
                  background: #CBD5E1;
                  border-radius: 2px;
                }
                .custom-thin-scrollbar::-webkit-scrollbar-thumb:hover {
                  background: #94A3B8;
                }
              `}} />
              
              <div className="flex gap-2 flex-1 min-h-0">
                {/* Tabs side-list */}
                <div className="w-[85px] border-r border-slate-100 flex flex-col gap-1 pr-1 overflow-y-auto custom-thin-scrollbar text-left">
                  {Object.keys(citiesData).map((tab) => {
                    const isSelected = activeCityTab === tab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveCityTab(tab)}
                        className={`text-left text-[11px] font-semibold px-2 py-1 rounded transition-all cursor-pointer truncate shrink-0 ${
                          isSelected
                            ? "bg-[var(--brand-accent)] text-white shadow-sm font-bold"
                            : "text-slate-500 hover:bg-slate-50 hover:text-slate-850"
                        }`}
                      >
                        {tab}
                      </button>
                    );
                  })}
                </div>
                
                {/* Cities scrollable list */}
                <div className="flex-1 overflow-y-auto custom-thin-scrollbar text-[10px] text-slate-400 leading-relaxed pr-1 text-justify scroll-smooth">
                  {citiesData[activeCityTab]}
                </div>
              </div>
            </div>
          </div>

        </div>

        <hr className="border-slate-200" />

        {/* Section 3: Bottom info area (Contact Information block & Social Media Icons side-by-side) */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 pb-6">
          {/* Contact Information Text block */}
          <div className="space-y-3 max-w-xl">
            <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wider">{LABELS.FOOTER.CONTACT_US_TITLE}</h4>
            {settings?.contact?.address ? (
              <p className="text-slate-700 text-xs md:text-sm font-medium leading-relaxed">
                <span className="font-semibold text-slate-900">{LABELS.BRAND.FULL_NAME}</span> — {settings.contact.address}, {[settings.contact.city, settings.contact.state, settings.contact.zip_code].filter(Boolean).join(", ")}, {settings.contact.country}
              </p>
            ) : (
              <p className="text-slate-700 text-xs md:text-sm font-medium leading-relaxed">
                <span className="font-semibold text-slate-900">{LABELS.BRAND.FULL_NAME}</span> — {LABELS.BRAND.DEFAULT_ADDRESS}, {LABELS.BRAND.DEFAULT_CITY}, {LABELS.BRAND.DEFAULT_STATE} {LABELS.BRAND.DEFAULT_ZIP}, {LABELS.BRAND.DEFAULT_COUNTRY}
              </p>
            )}
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs md:text-sm font-semibold text-slate-800 mt-2">
              <span className="flex items-center gap-2">
                {/* WhatsApp/Phone Greenish Icon */}
                <svg className="w-4 h-4 text-emerald-600 fill-current shrink-0" viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.458L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.42 9.864-9.852.002-2.63-1.023-5.101-2.885-6.967C16.528 1.967 14.063.943 11.998.943c-5.441 0-9.87 4.42-9.874 9.855-.001 1.761.472 3.483 1.368 5.031L2.482 21.52l5.165-1.366zM17.15 14.49c-.28-.14-1.65-.81-1.91-.9-.26-.1-.45-.14-.64.14-.19.28-.73.9-.9 1.1-.17.19-.34.21-.62.07-2.18-1.09-3.41-2.48-4.14-3.74-.19-.33-.02-.51.15-.68.15-.15.33-.38.49-.57.16-.19.22-.33.33-.55.11-.22.05-.41-.02-.55-.08-.14-.64-1.55-.88-2.11-.23-.57-.47-.49-.64-.5-.17-.01-.36-.01-.55-.01-.19 0-.5.07-.76.36-.26.28-1 .98-1 2.4s1.03 2.78 1.18 2.97c.15.19 2.03 3.1 4.92 4.35.69.29 1.23.47 1.65.6.69.22 1.32.19 1.82.11.56-.08 1.65-.67 1.88-1.32.23-.65.23-1.2.16-1.32-.07-.12-.26-.19-.54-.33z"/>
                </svg>
                Phone: <a href={`tel:${settings?.contact?.secondary_phone || settings?.contact?.phone || LABELS.BRAND.DEFAULT_PHONE}`} className="hover:text-[var(--brand-accent)] transition-colors">{settings?.contact?.secondary_phone || settings?.contact?.phone || LABELS.BRAND.DEFAULT_PHONE}</a>
              </span>
              <span className="flex items-center gap-2">
                {/* Envelope/Email Icon */}
                <svg className="w-4 h-4 text-slate-800 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                Email: <a href={`mailto:${settings?.contact?.email || LABELS.BRAND.DEFAULT_EMAIL}`} className="hover:text-[var(--brand-accent)] transition-colors">{settings?.contact?.email || LABELS.BRAND.DEFAULT_EMAIL}</a>
              </span>
            </div>
          </div>

          {/* Social Icons Connect Block */}
          <div className="space-y-2 shrink-0">
            <h4 className="font-semibold text-slate-950 text-xs text-left md:text-right">{LABELS.FOOTER.CONNECT_WITH_US_TITLE}</h4>
            <div className="flex gap-2">
              <a href="https://facebook.com" className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="Facebook">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M9 8H7v3h2v9h4v-9h3.6l.4-3H13V6c0-.5.5-1 1-1h2V1h-3c-3 0-5 2-5 5v2z"/></svg>
              </a>
              <a href="https://www.instagram.com/muthu_metals_corporation/?hl=en" className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="Instagram">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.051.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://twitter.com" className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="Twitter">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
              </a>
              <a href="https://youtube.com" className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="YouTube">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M23.498 6.163a3.003 3.003 0 00-2.11-2.11C19.518 3.545 12 3.545 12 3.545s-7.518 0-9.388.507a3.003 3.003 0 00-2.11 2.11C0 8.033 0 12 0 12s0 3.967.502 5.837a3.003 3.003 0 002.11 2.11c1.87.507 9.388.507 9.388.507s7.518 0 9.388-.507a3.003 3.003 0 002.11-2.11C24 15.967 24 12 24 12s0-3.967-.502-5.837zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://linkedin.com" className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="LinkedIn">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://pinterest.com" className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center hover:bg-[var(--brand-accent)] transition-all duration-300 shadow-sm" aria-label="Pinterest">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.168 1.777 2.168 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.166-1.495-.694-2.433-2.878-2.433-4.629 0-3.764 2.728-7.229 7.889-7.229 4.144 0 7.365 2.953 7.365 6.9 0 4.116-2.595 7.431-6.199 7.431-1.21 0-2.348-.63-2.738-1.373 0 0-.599 2.282-.746 2.833-.27.103-.997 1.248-1.5 2.081 1.075.316 2.207.487 3.385.487 6.622 0 11.988-5.366 11.988-11.985C23.985 5.367 18.618 0 12.017 0z"/></svg>
              </a>
            </div>
          </div>
        </div>

      </div>

      {/* Bottom Bar: Links and Copyright */}
      <div className="w-full bg-[#f1f5f9] border-t border-slate-200 pt-6 pb-[72px] md:py-6 mt-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center text-slate-500 gap-4">
       
          <div className="text-13 text-slate-400 font-medium">
            © {new Date().getFullYear()} {LABELS.FOOTER.DOMAIN}. {LABELS.FOOTER.COPYRIGHT_PREFIX}
          </div>
        </div>
      </div>
      {/* About Us Modal Popup */}
      {isAboutModalOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4"
          onClick={() => setIsAboutModalOpen(false)}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 md:p-8 shadow-2xl relative border border-slate-100 animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center pb-4 mb-4 border-b border-slate-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-lg text-slate-900">About {LABELS.BRAND.NAME}</h3>
              <button
                type="button"
                onClick={() => setIsAboutModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-sm transition-colors cursor-pointer"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="text-xs md:text-sm text-slate-600 leading-relaxed space-y-4">
              {settings?.general_settings?.about_us ? (
                <div 
                  className="leading-relaxed break-words"
                  dangerouslySetInnerHTML={{ __html: decodeHtml(settings.general_settings.about_us) }}
                />
              ) : (
                <p>
                  {LABELS.BRAND.ABOUT_FALLBACK}
                </p>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-slate-100 flex justify-end">
              <button
                type="button"
                onClick={() => setIsAboutModalOpen(false)}
                className="px-5 py-2 bg-slate-900 text-white text-xs font-semibold rounded-lg hover:bg-[var(--brand-accent)] transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}
